import type { SentimentLevel } from './types';
import i18n from './i18n';

// Email key: replace dots with || for Firebase RTDB paths
export function emailToKey(email: string): string {
  return email.replace(/\./g, '||');
}

export function keyToEmail(key: string): string {
  return key.replace(/\|\|/g, '.');
}

/**
 * Pull a usable avatar URL from a raw user record (cache/RTDB shape), trying the
 * known image fields in priority order and rejecting non-URL values. Mirrors the
 * resolveImg helpers in MainShell/EmployeeShell — keep in sync. Returns '' when none.
 */
export function resolveUserImage(raw: any): string {
  const img = raw?.profilepic_small || raw?.profilepic_crop || raw?.profilepic || raw?.user_img || raw?.userImg;
  return typeof img === 'string' && (img.startsWith('http://') || img.startsWith('https://')) ? img : '';
}

/**
 * Resolve a user's raw `team` field — usually a Firebase push id (e.g.
 * `-L03_NMxjQUY2RAhiemb`) — to a display name via the team-id→name map
 * (MainShell's `teamsMap`). Falls back to the raw value when it already looks like a
 * name (not a push id), else ''. Never returns an id.
 */
export function resolveTeamName(rawTeam: unknown, teamsMap?: Record<string, string>): string {
  const id = typeof rawTeam === 'string' ? rawTeam.trim() : '';
  if (id && teamsMap && teamsMap[id]) return teamsMap[id];
  if (id && !/^-[A-Za-z0-9_-]{15,}$/.test(id)) return id; // already a human name
  return '';
}

/**
 * `YYYY-MM-DD` for the date in the user's local timezone. `toISOString().slice(0, 10)`
 * keys by the UTC day instead, which is yesterday (or tomorrow) for part of every day
 * anywhere off UTC.
 */
export function formatLocalDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export const HAPPILY_DEFAULT_TIMEZONE = 7;

export function normalizeTimezone(
  timezone: number | string | null | undefined,
  fallback: number = HAPPILY_DEFAULT_TIMEZONE
): number {
  const value = typeof timezone === 'string' && timezone.trim() !== ''
    ? Number(timezone)
    : timezone;

  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

// Timestamp in the legacy format used by the mobile app for message sorting/display.
export function formatTimestamp(timezone: number | string | null | undefined = HAPPILY_DEFAULT_TIMEZONE): string {
  const now = new Date();
  const normalizedTimezone = normalizeTimezone(timezone);
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
  const adjusted = new Date(utcMs + normalizedTimezone * 3600000);

  const y = adjusted.getFullYear();
  const m = String(adjusted.getMonth() + 1).padStart(2, '0');
  const d = String(adjusted.getDate()).padStart(2, '0');
  const h = String(adjusted.getHours()).padStart(2, '0');
  const min = String(adjusted.getMinutes()).padStart(2, '0');
  const s = String(adjusted.getSeconds()).padStart(2, '0');

  return `${y}-${m}-${d} ${h}:${min}:${s}`;
}

// Relative time display
// 'Z' or a '+07:00'/'-0500' tail means the string states its own offset, so it
// already names an instant. Only offset-less strings — the
// 'YYYY-MM-DD HH:mm:ss' shape this product writes — need a frame supplied.
const EXPLICIT_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * The timezone stored timestamps are READ in. Set once from the session (see
 * App.tsx) so the fourteen timeAgo call sites do not each have to thread it.
 *
 * A naive 'YYYY-MM-DD HH:mm:ss' carries no offset, so it only means something
 * against an agreed frame, and across this product that frame is the USER's own
 * clock: the mobile client stamps every answer and reply with
 * users/{email}/timezone, formatTimestamp above stamps this app's replies with
 * session.timezone, and since 2026-08-19 the coach path on the server does the
 * same. Reading them as BROWSER-local instead is invisible only while the two
 * offsets agree — which is exactly what broke when a demo company ran at 5.5
 * with +7 users and a coach reply written 2 seconds after the answer rendered
 * as "1 hour ago".
 */
let viewerTimezone: number = HAPPILY_DEFAULT_TIMEZONE;

export function setViewerTimezone(timezone: number | string | null | undefined): void {
  viewerTimezone = normalizeTimezone(timezone);
}

export function getViewerTimezone(): number {
  return viewerTimezone;
}

/** Read a stored timestamp in the viewer's frame and return the instant it names. */
export function toDisplayDate(
  dateString: string,
  timezone: number | string | null | undefined = viewerTimezone
): Date {
  const raw = typeof dateString === 'string' ? dateString.trim() : '';
  if (!raw) return new Date(NaN);
  if (EXPLICIT_OFFSET.test(raw)) return new Date(raw.replace(' ', 'T'));

  const asUtc = Date.parse(`${raw.replace(' ', 'T')}Z`);
  if (Number.isNaN(asUtc)) return new Date(raw.replace(' ', 'T'));
  return new Date(asUtc - normalizeTimezone(timezone) * 3600000);
}

export function timeAgo(
  dateString: string,
  timezone: number | string | null | undefined = viewerTimezone
): string {
  const date = toDisplayDate(dateString, timezone);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return i18n.t('common:time.justNow');
  if (diffMin < 60) return i18n.t('common:time.minutesAgo', { count: diffMin });
  if (diffHr < 24) return i18n.t('common:time.hoursAgo', { count: diffHr });
  if (diffDay < 7) return i18n.t('common:time.daysAgo', { count: diffDay });
  if (diffDay < 30) return i18n.t('common:time.weeksAgo', { count: Math.floor(diffDay / 7) });
  return i18n.t('common:time.monthsAgo', { count: Math.floor(diffDay / 30) });
}

// Gem calculation from goal + culture ratings
export function calculateGems(goal: number, culture: number): number {
  if (goal === 4 && culture === 4) return 2;
  if (goal >= 3 && culture >= 3) return 1;
  return 0;
}

// Review status based on last review date and company config
export function getReviewStatus(
  gotGemLastdate: string | undefined,
  pendingMonths: number = 1,
  overdueMonths: number = 2,
  createdatetime?: string
): 'completed' | 'pending' | 'overdue' {
  const now = new Date();

  if (!gotGemLastdate) {
    // No review ever received — check account creation date
    if (createdatetime) {
      const createdDate = new Date(createdatetime.replace(' ', 'T'));
      const diffMs = now.getTime() - createdDate.getTime();
      const diffDays = Math.floor(diffMs / 86400000);
      if (diffDays < overdueMonths * 30) return 'pending';
    }
    return 'overdue';
  }

  const lastDate = new Date(gotGemLastdate.replace(' ', 'T'));
  const diffMs = now.getTime() - lastDate.getTime();
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffDays < pendingMonths * 30) return 'completed';
  if (diffDays < overdueMonths * 30) return 'pending';
  return 'overdue';
}

// Truncate text with ellipsis
export function truncate(text: string, maxLength: number): string {
  if (!text || text.length <= maxLength) return text || '';
  return text.substring(0, maxLength - 1) + '\u2026';
}

// Map a continuous check-in average (0–4 scale) to a SentimentLevel using thresholds
export function answerToSentiment(avg: number): SentimentLevel {
  if (avg <= 0.5) return 'Very Happy';
  if (avg <= 1.5) return 'Pretty Good';
  if (avg <= 2.5) return 'Just Okay';
  if (avg <= 3.5) return 'Not Great';
  return 'Terrible';
}

// Convert a 0–4 check-in average to a 0–100 happiness percentage
// 0 (Very Happy) → 100%, 4 (Terrible) → 0%
export function happinessFromAverage(avg: number): number {
  const clamped = Math.min(4, Math.max(0, avg));
  return Math.round(((4 - clamped) / 4) * 100);
}

// Most recent Saturday (YYYY-MM-DD) — used for weekly risk queries
export function getMostRecentSaturday(): string {
  const today = new Date();
  const day = today.getDay(); // 0=Sun, 6=Sat
  const daysSinceSat = day === 6 ? 0 : day + 1;
  const saturday = new Date(today);
  saturday.setDate(today.getDate() - daysSinceSat);
  // Format in local time to match the local-day arithmetic above: a UTC slice
  // hands a +7 user Friday's date until 07:00 on Saturday.
  return formatLocalDate(saturday);
}

// Reply insights score color
export function getInsightColor(score: number): string {
  if (score >= 80) return '#66BB6A';
  if (score >= 51) return '#FBC02D';
  return '#F44336';
}
